import { useState, useCallback } from 'react';
import imageCompression from 'browser-image-compression';

interface UseImageCompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  targetFormat?: 'image/png' | 'image/jpeg' | 'image/webp';
}

interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  preview: string;
}

/**
 * Extensão correspondente a cada tipo que o compressor sabe produzir. O backend
 * deduz o Content-Type do S3 a partir da extensão do nome enviado, então um
 * nome que mente sobre o formato vira uma imagem que não renderiza.
 */
const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/bmp': 'bmp',
};

/**
 * Devolve um File de verdade a partir do que o compressor retorna.
 *
 * `browser-image-compression@2` nunca constrói um File: toda saída dele é um
 * Blob com `name` grudado como propriedade solta, embora a tipagem do pacote
 * prometa File. O FormData ignora esse `name` e nomeia a parte como "blob" —
 * era assim que o upload de avatar chegava ao backend sem extensão nenhuma e
 * voltava 400. Só arquivos acima do orçamento passam pelo compressor, então a
 * falha atingia exatamente as fotos de celular.
 *
 * A extensão também é reescrita quando o compressor troca o formato: manter
 * "foto.jpg" num conteúdo WEBP faria o S3 servir o arquivo com o Content-Type
 * errado.
 */
function toNamedFile(compressed: Blob, originalName: string): File {
  const type = compressed.type || 'image/jpeg';
  const extension = EXTENSION_BY_MIME_TYPE[type];

  const baseName = originalName.replace(/\.[^.]+$/, '') || 'imagem';
  const name = extension ? `${baseName}.${extension}` : originalName;

  return new File([compressed], name, { type, lastModified: Date.now() });
}

export function useImageCompression(options?: UseImageCompressionOptions): {
  compress: (file: File) => Promise<CompressionResult>;
  isCompressing: boolean;
  error: string | null;
} {
  const [isCompressing, setIsCompressing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const compress = useCallback(
    async (file: File): Promise<CompressionResult> => {
      setIsCompressing(true);
      setError(null);

      const originalSize = file.size;

      try {
        const compressedBlob = await imageCompression(file, {
          maxSizeMB: options?.maxSizeMB ?? 0.2,
          maxWidthOrHeight: options?.maxWidthOrHeight ?? 512,
          useWebWorker: true,
          fileType: options?.targetFormat,
        });

        const compressedFile = toNamedFile(compressedBlob, file.name);

        // Gerar preview
        const preview = await new Promise<string>(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(compressedFile);
        });

        return {
          file: compressedFile,
          originalSize,
          compressedSize: compressedFile.size,
          compressionRatio: 1 - compressedFile.size / originalSize,
          preview,
        };
      } catch (err: any) {
        setError(err.message || 'Erro ao comprimir imagem');
        throw err;
      } finally {
        setIsCompressing(false);
      }
    },
    [options],
  );

  return {
    compress,
    isCompressing,
    error,
  };
}
